import { PaginatedResponse } from '../_DTOs/IPaginaterResponse';
import { NestedQueryDataRequest, StructuredDataRequest } from '../_DTOs/IStructuredData';
import { EntityBase } from '../_models/EntityBase';
import { IEntityMetaData, IPropretyAttribute, IMetaProprety } from '../_models/IEntityMetaData';
import { Register } from '../decorators/DependencyInjectionRegistreyDecorators';
import type { IEntityRepository } from '../repositories/IRepository';
import { IDataService } from './IDataService';
import { LookupService, LookupProfile, LookupContext } from './LookupService';
import { BrowserIdService } from './browser-id.service';
import { HttpHeaders } from '@angular/common/http';



@Register('ALL_ENTITIES')
export class DataService<T> implements IDataService<T> {


  constructor(
    private repository: IEntityRepository<T>,
    private lookupService: LookupService
  ) { }

  getMetaData(): IEntityMetaData {
    return this.repository.getMetaData();
  }

  async getAll(params: StructuredDataRequest, requiredLookups: LookupProfile[] = []): Promise<PaginatedResponse<T>> {
    // Convertir les profils en contextes de lookup
    const lookupContexts = this.lookupService.getRequiredLookups(requiredLookups);

    // Utiliser la nouvelle méthode qui renvoie aussi les lookups
    const response = await this.repository.getAllWithLookups(params, lookupContexts);

    // Mettre à jour le service de lookup avec les nouveaux contextes
    if (response.lookups) {
      this.lookupService.updateLookups(response.lookups);
    }

    // Retourner seulement les données paginées
    return {
      items: response.items,
      total: response.total,
      truncated: response.truncated,
      matchedIds: response.matchedIds
    };
  }

  // Interrogation directe de la base du client, pour les listes imbriquees.
  async queryNested(request: NestedQueryDataRequest): Promise<PaginatedResponse<T>> {
    return await this.repository.queryNested(request);
  }

  public async getLookupsOnly(requiredLookups: LookupProfile[]): Promise<void> {
    const lookupContexts = this.lookupService.getRequiredLookups(requiredLookups);
    const response = await this.repository.fetchLookups(lookupContexts);
    if (response && response.lookups) {
      this.lookupService.updateLookups(response.lookups);
    }
  }

  public async getLookupsByNames(lookupNames: string[]): Promise<void> {
    const lookupProfiles: LookupProfile[] = lookupNames.map(name => ({ lookupName: name }));
    return await this.getLookupsByProfiles(lookupProfiles);
  }
  public async getLookupsByProfiles(lookupProfiles: LookupProfile[]): Promise<void> {
    await this.getLookupsOnly(lookupProfiles);
  }


  async get(id: number): Promise<T> {
    return await this.repository.get(id);
  }

  async getBySearchArgs(params: StructuredDataRequest, search: any): Promise<PaginatedResponse<T>> {
    return await this.repository.getBySearchArgs(params, search);
  }

  async create(entity: T, componentId?: string): Promise<T> {
    return await this.repository.create(entity, componentId);
  }

  async update(id: number, entity: T, componentId?: string): Promise<void> {
    return await this.repository.update(id, entity, componentId);
  }

  async delete(id: number, componentId?: string): Promise<void> {
    return await this.repository.delete(id, componentId);
  }

  async execute<U>(method: string, urlExecuteMethod: string, params?: any): Promise<U|null> {
    return await this.repository.execute(method, urlExecuteMethod, params);
  }
  async download<U>(urlExecuteMethod: string, params?: any): Promise<U|null> {
    return await this.repository.download(urlExecuteMethod, params);
  }
  // Méthodes basées sur les métadonnées
  getFilterProperties(): string[] {
    return EntityBase.GetPropretiesByDecoratorFromMeta(
      this.getMetaData(),
      IPropretyAttribute.FilterColumn
    ).map((metaProp: IMetaProprety) => metaProp.PropretyName);
  }

  getSortProperties(): string[] {
    return EntityBase.GetPropretiesByDecoratorFromMeta(
      this.getMetaData(),
      IPropretyAttribute.SortColumn
    ).map((metaProp: IMetaProprety) => metaProp.PropretyName);
  }

  getKeyPropertyName(): string {
    return EntityBase.GetPropretiesByDecoratorFromMeta(
      this.getMetaData(),
      IPropretyAttribute.KeyProperty
    ).map((metaProp: IMetaProprety) => metaProp.PropretyName)[0];
  }
}
